import { adminApi, api } from "@/lib/api";

/** Read media duration (seconds) from a File using a hidden <audio>/<video>. */
export const readMediaDuration = (file) =>
  new Promise((resolve) => {
    const isVideo = file.type.startsWith("video/");
    const el = document.createElement(isVideo ? "video" : "audio");
    el.preload = "metadata";
    const url = URL.createObjectURL(file);
    const done = (v) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    el.onloadedmetadata = () => done(isFinite(el.duration) ? Math.round(el.duration) : null);
    el.onerror = () => done(null);
    el.src = url;
  });

/** Capture a JPEG thumbnail from a video File (~2s in). Returns a File or null. */
export const captureVideoThumbnail = (file) =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    const finish = (v) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    video.onerror = () => finish(null);
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(2, Math.max(0, (video.duration || 1) / 2));
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1280 / (video.videoWidth || 1280));
        canvas.width = Math.round((video.videoWidth || 1280) * scale);
        canvas.height = Math.round((video.videoHeight || 720) * scale);
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => finish(blob ? new File([blob], "thumb.jpg", { type: "image/jpeg" }) : null), "image/jpeg", 0.85);
      } catch {
        finish(null);
      }
    };
    video.src = url;
  });

/** Chunked upload → object storage via backend. onProgress(0..100). Returns {file_id, url, kind}.
 *  scope "admin" (default, authenticated, original quality) or "citizen" (public forms). */
export async function uploadMedia(file, { durationSeconds = null, onProgress = () => {}, scope = "admin" } = {}) {
  const client = scope === "citizen" ? api : adminApi;
  const base = scope === "citizen" ? "/uploads" : "/admin/media/uploads";
  const { data: init } = await client.post(`${base}/init`, {
    filename: file.name,
    content_type: file.type,
    size: file.size,
    duration_seconds: durationSeconds,
  });
  const { upload_id, chunk_size } = init;
  const total = Math.ceil(file.size / chunk_size);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * chunk_size, Math.min(file.size, (i + 1) * chunk_size));
    let attempt = 0;
    for (;;) {
      try {
        await client.put(`${base}/${upload_id}/chunk`, blob, {
          params: { index: i },
          headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (e) => {
            const sent = i * chunk_size + (e.loaded || 0);
            onProgress(Math.min(99, Math.round((sent / file.size) * 100)));
          },
        });
        break;
      } catch (err) {
        if (err?.response?.status === 409 || attempt >= 2) throw err;
        attempt += 1;
      }
    }
  }
  const { data } = await client.post(`${base}/${upload_id}/complete`);
  onProgress(100);
  return data;
}
